import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Boxes,
  CircuitBoard,
  Brain,
  PaintBucket,
  Network,
  Calendar,
  IndianRupee,
  Users,
  AlertTriangle,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Home,
  Cpu,
  Sparkles,
} from "lucide-react";
import { HyperGraphFlow } from "../components/graph/HyperGraphFlow";
import { ExpandableGraphFrame } from "../components/integrations/ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "../components/integrations/Graph3D";
// RuleBuilder + RuleInAction live exclusively at /rules — the
// cockpit Rules tab shows project status only.
import { ProjectRuleStatus } from "../components/rules/ProjectRuleStatus";
import { NodeDetail } from "../components/graph/NodeDetail";
import { WhyPanel } from "../components/why/WhyPanel";
import { CatalogGrid } from "../components/catalog/CatalogGrid";
import { MemoryDeck } from "../components/memory/MemoryDeck";
import { MoodBoard } from "../components/inspiration/MoodBoard";
import { RenderRoomPanel } from "../components/cockpit/RenderRoomPanel";
import { AgenticFlow } from "../components/agent/AgenticFlow";
import { BundlePending } from "../components/cockpit/BundlePending";
import { useActiveProjectGraph } from "../lib/useActiveProjectGraph";
import { RoomsView } from "../components/rooms/RoomsView";
// RuleLibrary is mounted on the top-menu /library route only.
// DataSourcesView moved to /admin/datasets
import { DesignerChat } from "../components/chat/DesignerChat";
import { InboxQueue } from "../components/inbox/InboxQueue";
import { ClientCopilot } from "../components/consumer/ClientCopilot";
import { SEED_ACTIVITY } from "../data/activity";
// SAMPLE_PROJECT no longer imported directly — every consumer routes
// through getActiveProjectGraph() so user-created projects light up.
import { PROJECTS, STATUS_COLOR, STATUS_LABEL, type ProjectSummary } from "../data/projects";
import { photo, type PhotoKey } from "../data/imagery";
import { SAMPLE_PROJECT as SP_FOR_NAV } from "../data/sampleGraph";
import { tabForKind } from "../lib/tabRouter";
// HERO_PHOTO no longer used — ProjectHero pulls the active project's
// thumbnail dynamically via photo() / photoForRoom().
import { SCENARIOS, DEFAULT_SCENARIO_ID, type Scenario, type ScenarioStep } from "../lib/scenario";
import { genScenarioTitle, genScenarioSubtitle, genScenarioOutcome, scenarioUrgency, routeFreeTextToScenario, SCENARIO_GENERATORS } from "../lib/scenarioGenerators";
import { ScenarioPreviewGraph } from "../components/cockpit/ScenarioPreviewGraph";
import { useScenario } from "../hooks/useScenario";
import { cls } from "../lib/cls";
import { env } from "../lib/env";
import type { CanvasNode } from "../types/rule";
// DesignerRule + designerRuleToCanvas are used by the top-menu
// /library route only — the cockpit reads applied IDs via localStorage.
import { getActiveAlertCount, getProjectAlertCount } from "../lib/studioFromGraph";
import { smoothScrollToSelector, smoothScrollTo } from "../lib/smoothScroll";
import { getActiveProjectGraph } from "../data/sampleGraph";

const DEFAULT_CANVAS: CanvasNode[] = [
  { id: "n-when",    kind: "WHEN",    position: [40, 40],  data: { kind: "WHEN", event: "onBOQRowAdded" } },
  {
    id: "n-if", kind: "IF", position: [40, 170],
    data: {
      kind: "IF",
      predicate: {
        kind: "cmp",
        lhs: { kind: "field", path: "SKU.leadDays", type: "number", unit: "days" },
        op: ">",
        rhs: { kind: "lit", type: "number", value: 60 },
      },
    },
  },
  { id: "n-then",    kind: "THEN",    position: [40, 320], data: { kind: "THEN", action: { kind: "substitute", criteria: { maxLeadDays: 21, minStyleMatch: 0.85, maxPriceDeltaPct: 0.15 } } } },
  { id: "n-because", kind: "BECAUSE", position: [40, 460], data: { kind: "BECAUSE", template: "Lead-time risks the milestone." } },
];

function loadCanvas(): CanvasNode[] {
  try {
    const raw = localStorage.getItem(env.VITE_LS_RULE_CANVAS);
    if (!raw) return DEFAULT_CANVAS;
    const parsed = JSON.parse(raw) as CanvasNode[];
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_CANVAS;
    return parsed;
  } catch {
    return DEFAULT_CANVAS;
  }
}

function loadAppliedIds(): string[] {
  try {
    const raw = localStorage.getItem(env.VITE_LS_APPLIED_RULES);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

type Tab = "rooms" | "catalog" | "inspiration" | "rules" | "memory" | "agent" | "graph";

type TabAudience = "shared" | "studio";
const TABS: { id: Tab; label: string; icon: JSX.Element; sub: string; audience: TabAudience }[] = [
  // Client-shareable — these are safe to surface to homeowners.
  { id: "rooms",       label: "Rooms",        sub: "Every space + its placements",  audience: "shared", icon: <Home className="w-3.5 h-3.5" /> },
  { id: "catalog",     label: "Catalog",      sub: "Items, costs, lead times",       audience: "shared", icon: <Boxes className="w-3.5 h-3.5" /> },
  { id: "inspiration", label: "Inspiration",  sub: "Mood, refs, intent",              audience: "shared", icon: <PaintBucket className="w-3.5 h-3.5" /> },
  { id: "memory",      label: "Memory",       sub: "What the client already told us", audience: "shared", icon: <Brain className="w-3.5 h-3.5" /> },
  // Studio-only — internal reasoning + ontology surfaces.
  // Rules · cockpit = project-aware status only. Library + Builder
  // live at the top menu (/library and /rules) — never duplicated here.
  { id: "rules",       label: "Rules",        sub: "What fired on this project",  audience: "studio", icon: <CircuitBoard className="w-3.5 h-3.5" /> },
  { id: "agent",       label: "Agent",        sub: "What it did, in context",     audience: "studio", icon: <Cpu className="w-3.5 h-3.5" /> },
  { id: "graph",       label: "Project map",  sub: "Everything, connected",       audience: "studio", icon: <Network className="w-3.5 h-3.5" /> },
  // Datasets moved to Admin — internal infra, not designer-facing.
];

export default function Landing(): JSX.Element {
  const [tab, setTab] = useState<Tab>("rooms");
  const [manualHighlight] = useState<string[]>([]);
  const [focusedNodeId, setFocusedNodeId] = useState<string>("al1");
  const [scenarioState, scenarioCtl] = useScenario();
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>(DEFAULT_SCENARIO_ID);
  const [currentProjectId, setCurrentProjectId] = useState<string>(() => {
    const fallback = PROJECTS[0]?.id ?? "";
    try {
      return localStorage.getItem(env.VITE_LS_ACTIVE_PROJECT)
          ?? localStorage.getItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY)
          ?? fallback;
    } catch { return fallback; }
  });
  // Keep the global picker (header ProjectTracker) in sync if it
  // changes the project from another route.
  useEffect(() => {
    function onPick(e: Event): void {
      const id = (e as CustomEvent<{ id: string }>).detail?.id;
      if (id && id !== currentProjectId) setCurrentProjectId(id);
    }
    window.addEventListener("foyrmind:projectChanged", onPick as EventListener);
    return () => window.removeEventListener("foyrmind:projectChanged", onPick as EventListener);
  }, [currentProjectId]);
  // When the cockpit's local picker changes the project, mirror it
  // into BOTH localStorage keys (env-driven) AND notify other pages
  // so Studio / Integrations / Rules re-derive instantly.
  useEffect(() => {
    try {
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY, currentProjectId);
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT,        currentProjectId);
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("foyrmind:projectChanged", { detail: { id: currentProjectId } }));
  }, [currentProjectId]);

  // Room cards open RoomDetailSheet → "Render this room" CTA fires
  // foyrmind:openRenderRoom; jump to the cockpit Render panel.
  useEffect(() => {
    function onOpenRender(): void { setTab("rooms"); }
    window.addEventListener("foyrmind:openRenderRoom", onOpenRender);
    return () => window.removeEventListener("foyrmind:openRenderRoom", onOpenRender);
  }, []);

  // Rule canvas — owned here so Library can write into it and changes persist.
  const [canvas, setCanvas] = useState<CanvasNode[]>(() => loadCanvas());
  const [appliedRuleIds, setAppliedRuleIds] = useState<string[]>(() => loadAppliedIds());

  useEffect(() => {
    try { localStorage.setItem(env.VITE_LS_RULE_CANVAS, JSON.stringify(canvas)); } catch { /* ignore */ }
  }, [canvas]);
  useEffect(() => {
    try { localStorage.setItem(env.VITE_LS_APPLIED_RULES, JSON.stringify(appliedRuleIds)); } catch { /* ignore */ }
  }, [appliedRuleIds]);

  // Rule addition happens at /library (top menu). Cockpit only reads
  // applied IDs via loadAppliedIds() above.
  void setCanvas; void setAppliedRuleIds;

  // Chat citations / Why-engine evidence: focus a node AND route the tab
  // to wherever that kind naturally lives.
  const focusNodeWithTabRoute = useCallback((nodeId: string) => {
    setFocusedNodeId(nodeId);
    const node = SP_FOR_NAV.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    setTab(tabForKind(node.kind) as Tab);
  }, []);

  const activeStep = scenarioState.step;
  useEffect(() => {
    if (!activeStep) return;
    if (activeStep.highlightTab) setTab(activeStep.highlightTab as Tab);
    if (activeStep.focusNodeId) setFocusedNodeId(activeStep.focusNodeId);
  }, [activeStep]);

  const highlightSet = useMemo(() => {
    if (activeStep && activeStep.highlightNodes.length > 0) {
      return new Set(activeStep.highlightNodes);
    }
    return new Set(manualHighlight);
  }, [activeStep, manualHighlight]);

  const selectedScenario: Scenario | undefined = SCENARIOS.find((s) => s.id === selectedScenarioId);

  return (
    <div className="max-w-[1480px] mx-auto px-4 sm:px-6 pt-6 pb-12">
      <ProjectPicker currentId={currentProjectId} onSwitch={setCurrentProjectId} />

      <ProjectHero
        projectId={currentProjectId}
        alertCount={getActiveAlertCount()}
        onClickAlerts={() => {
          // Pull the active project's typed graph — same source the alert
          // count derives from — and focus the first thing worth looking at.
          // Priority: explicit Alert node > blocked Milestone > stale
          // Approval > the project itself. Always *do* something.
          const g = getActiveProjectGraph();
          const buffer = env.VITE_LEAD_TIME_BUFFER_DAYS;
          const skus = g.nodes.filter((n: { kind: string }) => n.kind === "SKU");
          const explicit = g.nodes.find((n: { kind: string }) => n.kind === "Alert");
          const blockedMs = g.nodes.find((n: { kind: string; attrs?: Record<string, unknown> }) => {
            if (n.kind !== "Milestone") return false;
            const dueIn = typeof n.attrs?.dueIn === "number" ? n.attrs.dueIn : null;
            if (dueIn == null) return false;
            const safe = dueIn - buffer;
            return skus.some((s: { attrs?: Record<string, unknown> }) => typeof s.attrs?.leadDays === "number" && (s.attrs.leadDays as number) > safe);
          });
          const staleAppr = g.nodes.find((n: { kind: string; attrs?: Record<string, unknown> }) => {
            if (n.kind !== "Approval") return false;
            const age = typeof n.attrs?.ageDays === "number" ? (n.attrs.ageDays as number) : 0;
            return age >= env.VITE_APPROVAL_AGE_DAYS;
          });
          const focus = explicit ?? blockedMs ?? staleAppr ?? g.nodes[0];
          if (focus) {
            focusNodeWithTabRoute(focus.id);
            // Apple-style smooth scroll to the scenario strip — uses
            // cubic-bezier easing, cancels on user wheel/touch.
            requestAnimationFrame(() => {
              if (!smoothScrollToSelector('[data-scenario-strip]', { offset: 72 })) {
                smoothScrollTo(320, { offset: 0 });
              }
            });
          }
        }}
      />

      <ScenarioStripGated
        scenarios={SCENARIOS}
        selectedId={selectedScenarioId}
        onSelect={setSelectedScenarioId}
        state={scenarioState}
        onPlay={() => selectedScenario && scenarioCtl.play(selectedScenario)}
        onPause={scenarioCtl.pause}
        onReset={scenarioCtl.reset}
      />

      <TabBar tab={tab} onChange={setTab} />

      <div
        className="mt-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_var(--rail-w)] [column-gap:var(--gap)] [row-gap:var(--gap)]"
        style={{
          // CLAUDE.md §1 — pulled from env, never a literal in markup.
          // 420px right rail + 28px gap reads as Apple-grade spacing
          // on a 1440 viewport without breaking down at 1280.
          ["--rail-w"  as never]: `${env.VITE_COCKPIT_RAIL_W_PX}px`,
          ["--gap"     as never]: `${env.VITE_COCKPIT_GAP_PX}px`,
        }}
      >
        <MainPane
          tab={tab}
          highlightSet={highlightSet}
          onNodeFocus={setFocusedNodeId}
          appliedRuleIds={appliedRuleIds}
          focusedNodeId={focusedNodeId}
          {...(activeStep ? { activeStageHint: activeStep.kind } : {})}
        />

        <aside className="min-w-0 space-y-3">
          <NodeDetail
            nodeId={focusedNodeId}
            onClose={() => setFocusedNodeId("")}
            onNavigate={setFocusedNodeId}
          />
          <SideRail
            focusedNodeId={focusedNodeId}
            onFocusChange={setFocusedNodeId}
            events={scenarioState.events}
          />
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────── Project picker (above hero) ─── */

function ProjectPicker({ currentId, onSwitch }: { currentId: string; onSwitch: (id: string) => void }): JSX.Element {
  const [showNew, setShowNew] = useState(false);
  return (
    <div className="panel p-2 mb-3 flex items-center gap-2 overflow-x-auto">
      <div className="flex flex-col items-start shrink-0 mr-1">
        <span className="eyebrow leading-tight">Your studio</span>
        <span className="text-[10px] text-ink-500 leading-tight tabular">{PROJECTS.length} projects</span>
      </div>
      <button
        onClick={() => setShowNew(true)}
        title="Create a new project"
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foyr text-cream hover:bg-foyr-deep transition shadow-soft text-[12px] font-semibold"
      >
        <span className="inline-block w-4 h-4 rounded-full bg-cream/20 grid place-items-center text-[14px] leading-none">+</span>
        New project
      </button>
      <NewProjectDialog open={showNew} onClose={() => setShowNew(false)} />
      <span className="h-9 w-px bg-border mx-1 shrink-0" />
      <div className="flex items-center gap-1.5">
        {PROJECTS.map((p) => {
          const active = p.id === currentId;
          return (
            <button
              key={p.id}
              onClick={() => onSwitch(p.id)}
              className={cls(
                "shrink-0 flex items-center gap-2 px-2 py-1.5 rounded-xl border transition-all",
                active
                  ? "bg-ink-950 text-cream border-ink-950"
                  : "bg-cream-dark border-border text-ink-950 hover:border-foyr/40",
              )}
              title={`${p.name} · ${p.city}`}
            >
              <img
                src={photo(p.thumbnailKey as PhotoKey, 56, 56)}
                alt=""
                className="w-7 h-7 rounded-md object-cover shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
              <div className="text-left leading-tight min-w-0">
                <div className={cls("text-[12px] font-semibold truncate", active ? "text-cream" : "text-ink-950")}>
                  {p.name} <span className={cls("font-normal", active ? "text-cream/70" : "text-ink-500")}>· {p.bhk}</span>
                </div>
                <div className={cls("text-[10px] truncate", active ? "text-cream/70" : "text-ink-500")}>
                  {p.city} · {getProjectAlertCount(p.id)} alert{getProjectAlertCount(p.id) === 1 ? "" : "s"}
                </div>
              </div>
              {getProjectAlertCount(p.id) > 0 ? (
                <span className={cls(
                  "ml-1 w-5 h-5 rounded-full grid place-items-center text-[10px] font-semibold tabular shrink-0",
                  active ? "bg-foyr text-cream" : "bg-foyr/15 text-foyr-deep",
                )}>
                  {getProjectAlertCount(p.id)}
                </span>
              ) : null}
              <span className={cls("hidden lg:inline pill text-[9px] shrink-0", STATUS_COLOR[p.status])}>
                {STATUS_LABEL[p.status]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element | null {
  // Hooks must run unconditionally to stay rule-compliant.
  const [brief, setBrief] = useState("");
  const [name, setName]     = useState("");
  const [city, setCity]     = useState("");
  const [type, setType]     = useState("3BHK");
  const [client, setClient] = useState("");
  const [budget, setBudget] = useState("");
  const [handover, setHandover] = useState("");
  const [importing, setImporting] = useState(false);
  const [coverage, setCoverage] = useState<number | null>(null);
  const [importedFile, setImportedFile] = useState<{ name: string; size: number } | null>(null);

  if (!open) return null;

  async function onBundleSelected(file: File | null): Promise<void> {
    if (!file) return;
    setImportedFile({ name: file.name, size: file.size });
    // Run the filename stem through the SAME DocAST extractor used for
    // briefs. The extractor's typed tokens (Address, Count, ProperNoun)
    // do the work — no per-field regex in this file. When the live
    // adapter is wired, the actual scene/BOQ/moodboard content replaces
    // the stem and the same plan shape comes back.
    const stem = file.name.replace(/\.[^.]+$/, "").replace(/[._-]+/g, " ").trim();
    const { extractFromBrief } = await import("../lib/ingest/extract");
    const { IRI } = await import("../lib/ingest/required");
    const plan = extractFromBrief(stem, file.name);
    // Look up by ontology IRI — never by label string.
    const byIri = (iri: string): string | undefined =>
      plan.resolved.find((r) => r.ontology_iri === iri)?.value;
    if (!name && stem) setName(stem);
    const cityV = byIri(IRI.city);   if (cityV && !city)  setCity(cityV);
    const bed   = byIri(IRI.bedrooms); if (bed && !type)  setType(`${bed}BHK`);
  }

  async function autoImport(): Promise<void> {
    if (!brief.trim()) return;
    setImporting(true);
    try {
      const { extractFromBrief } = await import("../lib/ingest/extract");
      const plan = extractFromBrief(brief.trim());
      const get = (label: string): string | undefined => plan.resolved.find((r) => r.label === label)?.value;
      const kind = get("Project kind") ?? "";
      const bedrooms = get("Number of bedrooms");
      const area = get("Carpet area (sqft)");
      const cityV = get("Project city");
      const date = get("Move-in date");
      const tier = get("Budget tier");
      if (cityV) setCity(cityV);
      if (bedrooms && (kind === "residential" || kind === "")) setType(`${bedrooms}BHK`);
      if (date) {
        // Coerce "2026-10" → "2026-10-01" for the date input.
        const iso = /^\d{4}-\d{2}$/.test(date) ? `${date}-01` : date;
        setHandover(iso);
      }
      // Fill name from sentence containing client cue, fallback to city.
      const occupants = get("Primary occupants") ?? "";
      const firstSentence = brief.split(/[.\n]/)[0] ?? "";
      const properMatch = firstSentence.match(/\b([A-Z][a-z]+)(?:\s*&\s*([A-Z][a-z]+))?/);
      if (properMatch && !client) setClient(properMatch[2] ? `${properMatch[1]} & ${properMatch[2]}` : properMatch[1] ?? "");
      if (!name && cityV) setName(`${cityV} · ${bedrooms ?? ""}${bedrooms ? "BHK" : "project"}`.trim());
      // Map tier to a placeholder budget so the field isn't empty.
      if (!budget && tier === "luxury")   setBudget("30000000");
      if (!budget && tier === "premium")  setBudget("16000000");
      if (!budget && tier === "standard") setBudget("6000000");
      setCoverage(plan.coverage);
      // Avoid unused-var warning while leaving the doc structure available for later.
      void occupants; void area;
    } finally {
      setImporting(false);
    }
  }

  return createPortal((
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-ink-950/50 backdrop-blur-sm p-4 overflow-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="panel-hero p-6 w-[520px] max-w-full bg-cream shadow-deep"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="eyebrow">New project</div>
        <div className="display-3 mt-1 text-ink-950">Wire it to the brain.</div>

        <div className="mt-4 rounded-lg border border-foyr/30 bg-foyr/5 p-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-foyr" />
            <span className="text-[11px] uppercase tracking-wider text-foyr-deep">Auto-import from a brief</span>
            {coverage !== null ? (
              <span className="pill bg-fern/15 border border-fern/40 text-fern text-[10px] tabular ml-auto">
                {Math.round(coverage * 100)}%
              </span>
            ) : null}
          </div>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Paste a client brief…"
            rows={3}
            className="w-full bg-cream border border-border rounded px-2 py-1.5 text-[12.5px] font-mono focus:outline-none focus:ring-2 focus:ring-foyr/30"
          />
          <div className="flex items-center justify-end mt-1.5">
            <button
              type="button"
              onClick={autoImport}
              disabled={!brief.trim() || importing}
              className="btn btn-outline !text-[11px] !py-1"
            >
              <Sparkles className={cls("w-3 h-3", importing && "animate-pulse")} />
              {importing ? "Mapping…" : "Auto-fill fields"}
            </button>
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-sea/30 bg-sea/5 p-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Boxes className="w-3.5 h-3.5 text-sea" />
            <span className="text-[11px] uppercase tracking-wider text-sea">Import from a project file</span>
            {importedFile ? (
              <span className="pill bg-fern/15 border border-fern/40 text-fern text-[10px] tabular ml-auto truncate max-w-[180px]">
                {importedFile.name}
              </span>
            ) : null}
          </div>
          <div className="text-[11px] text-ink-700 mb-1.5">
            Scene · BOQ · moodboard from your existing 3D-design tool. Parsed via the per-format adapter (USD · IFC · DXF · PDF · XLSX).
          </div>
          <label className="block cursor-pointer">
            <input
              type="file"
              accept=".usd,.usdz,.ifc,.dxf,.dwg,.pdf,.xlsx,.csv,.zip,.json"
              className="hidden"
              onChange={(e) => onBundleSelected(e.target.files?.[0] ?? null)}
            />
            <span className="btn btn-outline !text-[11px] !py-1 w-full justify-center">
              <Boxes className="w-3 h-3" /> {importedFile ? "Replace file" : "Choose bundle…"}
            </span>
          </label>
        </div>

        <form className="mt-4 space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); onClose(); }}>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-500">Project name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2" placeholder="e.g. Marine Drive Studio" required />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-ink-500">City</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2" placeholder="Mumbai" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-ink-500">Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)} className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2">
                <option>1BHK</option><option>2BHK</option><option>3BHK</option><option>4BHK</option><option>Villa</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-500">Client</span>
            <input value={client} onChange={(e) => setClient(e.target.value)} className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2" placeholder="Family name" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-ink-500">Budget (₹)</span>
              <input value={budget} onChange={(e) => setBudget(e.target.value)} type="number" className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2 tabular" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-ink-500">Handover</span>
              <input value={handover} onChange={(e) => setHandover(e.target.value)} type="date" className="mt-1 w-full bg-cream border border-border rounded-lg px-3 py-2" />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-outline">Cancel</button>
            <button type="submit" className="btn btn-foyr">Create project</button>
          </div>
        </form>
      </motion.div>
    </div>
  ), document.body);
}

/* ─────────────────────────────────────────── Hero ─── */

function ProjectHero({
  projectId,
  alertCount = 0,
  onClickAlerts,
}: {
  projectId: string;
  alertCount?: number;
  onClickAlerts?: () => void;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  // Look up the active project from the projects roster — every visible
  // value below is now derived from this lookup. User-created projects
  // (Onboard → Foyr import / Create) live in localStorage and merge in
  // at runtime so the hero renders the project the switcher shows.
  const userMatch = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      if (!raw) return null;
      const arr = JSON.parse(raw) as ProjectSummary[];
      return Array.isArray(arr) ? arr.find((x) => x.id === projectId) ?? null : null;
    } catch { return null; }
  }, [projectId]);
  const p = userMatch ?? PROJECTS.find((x) => x.id === projectId) ?? PROJECTS[0];
  if (!p) return <></>;
  // Reset failure state when project changes (key on projectId).
  return (
    <section key={projectId} className="relative overflow-hidden rounded-[28px] panel-hero">
      <div className="relative aspect-[21/9] sm:aspect-[21/8] w-full">
        {failed ? (
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#FCE6D2,#E8B591 45%,#A04612)" }} />
        ) : (
          <img
            src={photo(p.thumbnailKey as PhotoKey, 1600, 900)}
            onError={() => setFailed(true)}
            alt={p.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-ink-950/15 to-transparent pointer-events-none" />
        <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-8">
          <div className="text-[11px] uppercase tracking-[0.16em] text-cream/70">Project</div>
          <h1 className="display-1 text-cream max-w-2xl">
            {p.name}
            {p.bhk && p.bhk !== "—" ? <> {p.bhk}</> : null}
            {p.city && p.city !== "—" ? <> · {p.city}</> : null}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {p.client && p.client !== "—" ? (
              <HeroChip icon={<Users className="w-3.5 h-3.5" />} label={p.client} />
            ) : null}
            {(() => { const d = formatHandover(p.deadline); return d ? (
              <HeroChip icon={<Calendar className="w-3.5 h-3.5" />} label={d} />
            ) : null; })()}
            {p.budgetINR > 0 ? (
              <HeroChip icon={<IndianRupee className="w-3.5 h-3.5" />} label={p.budgetINR.toLocaleString("en-IN")} />
            ) : null}
            <HeroChip
              icon={<Network className="w-3.5 h-3.5" />}
              label={`${getActiveProjectGraph().nodes.length} nodes`}
            />
            <HeroChip
              icon={<AlertTriangle className="w-3.5 h-3.5" />}
              label={`${alertCount} ${alertCount === 1 ? "alert" : "alerts"}`}
              tone="warn"
              {...(onClickAlerts ? { onClick: onClickAlerts } : {})}
            />
            {(() => {
              if (!p.id.startsWith("foyr-")) return null;
              // Bundle pending only until the Onboarding Agent has
              // written a real typed graph for this project. After
              // ingest, flip the pill to "bundle ingested · N nodes".
              let ingestedNodes = 0;
              try {
                const raw = typeof window !== "undefined" ? window.localStorage.getItem(env.VITE_LS_PROJECT_GRAPHS) : null;
                if (raw) {
                  const map = JSON.parse(raw) as Record<string, { graph?: { nodes?: unknown[] } }>;
                  const g = map?.[p.id]?.graph;
                  if (g?.nodes && Array.isArray(g.nodes) && g.nodes.length >= 2) ingestedNodes = g.nodes.length;
                }
              } catch { /* ignore */ }
              return ingestedNodes > 0 ? (
                <HeroChip
                  icon={<Network className="w-3.5 h-3.5" />}
                  label={`Foyr Neo · bundle ingested · ${ingestedNodes} nodes`}
                />
              ) : (
                <HeroChip
                  icon={<AlertTriangle className="w-3.5 h-3.5" />}
                  label="Linked from Foyr Neo · bundle pending"
                  tone="warn"
                />
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}

function formatHandover(iso: string): string {
  if (!iso || iso === "—") return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

function HeroChip({ icon, label, tone, onClick }: { icon: JSX.Element; label: string; tone?: "warn"; onClick?: () => void }): JSX.Element {
  const klass = cls(
    "pill backdrop-blur-md border transition",
    tone === "warn" ? "bg-foyr/90 text-cream border-foyr" : "bg-cream/15 text-cream border-cream/25",
    onClick && "cursor-pointer hover:brightness-110 hover:scale-[1.03]",
  );
  if (onClick) {
    return (
      <button onClick={onClick} className={klass} title="Open the Why-engine on the first alert">
        {icon}{label}
      </button>
    );
  }
  return <span className={klass}>{icon}{label}</span>;
}

/* ─────────────────────────────────────────── Scenario strip ─── */

/* Foyr-linked projects without a real bundle: don't show the
 * canonical Riverside scenario theatre. Render a small inline
 * "scenarios will run on your bundle" notice instead — keeps the
 * cockpit honest. */
function ScenarioStripGated(props: React.ComponentProps<typeof ScenarioStrip>): JSX.Element {
  const { isLinkedPending } = useActiveProjectGraph();
  if (isLinkedPending) {
    return (
      <div className="mt-4 panel-subtle p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12.5px] text-ink-700 leading-snug">
          <span className="font-semibold text-ink-950">Scenarios pause for linked Foyr projects.</span>{" "}
          Riverside-derived what-ifs would mislead — they run on your real bundle once it ingests.
          The Agent / Rules tabs show the live reasoning the agent <em>can</em> do right now.
        </div>
        <span className="pill text-[10.5px] bg-foyr/10 text-foyr-deep border border-foyr/30">
          live reasoning · no canonical theatre
        </span>
      </div>
    );
  }
  return <ScenarioStrip {...props} />;
}

function ScenarioStrip({
  scenarios,
  selectedId,
  onSelect,
  state,
  onPlay,
  onPause,
  onReset,
}: {
  scenarios: Scenario[];
  selectedId: string;
  onSelect: (id: string) => void;
  state: ReturnType<typeof useScenario>[0];
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
}): JSX.Element {
  const selected = scenarios.find((s) => s.id === selectedId) ?? scenarios[0];
  if (!selected) return <></>;
  const total = state.totalSteps || selected.steps.length;
  const progress = state.done ? 1 : state.stepIndex < 0 ? 0 : (state.stepIndex + 1) / Math.max(1, total);

  function pickAndPlay(id: string): Scenario | null {
    onSelect(id);
    const s = scenarios.find((x) => x.id === id);
    if (s) onPlay();
    return s ?? null;
  }

  return (
    <div data-scenario-strip className="mt-4 panel p-4 sm:p-5 space-y-4 scroll-mt-20">
      {/* Header — eyebrow + title + dynamic subtitle. */}
      <div>
        <div className="eyebrow inline-flex items-center gap-1.5">
          Try a what-if · live from your project
        </div>
        <div className="display-3 mt-0.5 text-ink-950">{genScenarioTitle(selected.id) ?? selected.title}</div>
        <div className="text-xs text-ink-500 mt-0.5">
          {genScenarioSubtitle(selected.id) ?? selected.subtitle}
        </div>
        <div className="mt-3">
          <AnimatePresence mode="wait">
            {state.step ? (
              <motion.div
                key={state.step.id}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.25 }}
                className="flex flex-wrap items-center gap-2 text-sm"
              >
                <span className={cls(
                  "pill shrink-0",
                  state.step.badge === "warn"  ? "bg-foyr text-cream" :
                  state.step.badge === "ok"    ? "bg-fern text-cream" :
                  state.step.badge === "money" ? "bg-ink-950 text-cream" :
                                                 "bg-cream-dark border border-border text-ink-700",
                )}>
                  {state.stepIndex + 1}/{total}
                </span>
                <span className="font-medium text-ink-950">{state.step.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-ink-300 shrink-0" />
                <span className="text-ink-500">{state.step.detail}</span>
                {state.step.delta ? (
                  <span className="pill bg-foyr/10 text-foyr-deep tabular ml-auto">{state.step.delta}</span>
                ) : null}
              </motion.div>
            ) : (
              <ScenarioIdlePreview selected={selected} />
            )}
          </AnimatePresence>
        </div>
        <div className="mt-3 h-1 w-full bg-border rounded-full overflow-hidden">
          <motion.div className="h-full bg-foyr" animate={{ width: `${progress * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      {/* Action row — Play / Reset on the left, Ask box on the right. */}
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex items-center gap-1.5 shrink-0">
          {state.running ? (
            <button onClick={onPause} className="btn btn-primary !text-xs !py-2" title="Pause the walkthrough">
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          ) : (
            <button onClick={onPlay} className="btn btn-foyr !text-xs !py-2" title="Step through this scenario">
              <Play className="w-3.5 h-3.5" /> {state.done ? "Replay" : "Play scenario"}
            </button>
          )}
          <button onClick={onReset} className="btn btn-outline !text-xs !py-2" title="Start over">
            <RotateCcw className="w-3 h-3" /> Start over
          </button>
        </div>
        <AskScenarioInline onResolve={pickAndPlay} />
      </div>

      {/* Scenario picker — chips are the primary picker. Active one is highlighted,
          urgency flag rides along, click = select + play. */}
      <ScenarioChips
        scenarios={scenarios}
        selectedId={selectedId}
        onPick={(id) => { onSelect(id); onPlay(); }}
      />
    </div>
  );
}

/* ─── Idle preview that fills the panel before Play ────────── */

function ScenarioIdlePreview({ selected }: { selected: Scenario }): JSX.Element {
  // Pull the FULL generator output — every step the play path will
  // walk — so the user sees the complete reasoning chain laid out
  // before they press Play.
  const { steps, fallback } = useMemo<{ steps: readonly import("../lib/scenario").ScenarioStep[]; fallback: boolean }>(() => {
    try {
      const gen = SCENARIO_GENERATORS[selected.id];
      if (gen) {
        const ss = gen();
        if (ss.length > 0) return { steps: ss, fallback: false };
      }
    } catch { /* fall through */ }
    return { steps: selected.steps, fallback: true };
  }, [selected.id, selected.steps]);

  const outcome = useMemo(() => genScenarioOutcome(selected.id) ?? selected.outcome, [selected.id, selected.outcome]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-3"
    >
      <div className="flex items-center gap-1.5 text-[11px] text-ink-500">
        <Sparkles className="w-3 h-3 text-foyr" />
        Press <strong className="text-ink-950">Play scenario</strong> to step through this what-if.
        {fallback ? <span className="text-mulberry">· static preview · live graph silent</span> : null}
      </div>
      {/* Apple-style xyflow graph — Reasoning chain top-to-bottom. */}
      <ScenarioPreviewGraph steps={steps} activeIndex={-1} fallback={fallback} />
      {outcome ? (
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="font-newyork text-[10.5px] uppercase tracking-[0.18em] text-ink-500">Outcome</span>
          <span className="pill bg-foyr/10 border border-foyr/30 text-foyr-deep tabular">{outcome}</span>
        </div>
      ) : null}
    </motion.div>
  );
}

/* ─── Free-text scenario input ────────────────────────────── */

// (ASK_EXAMPLES retired — the ScenarioChips row is now the primary picker.)

function AskScenarioInline({
  onResolve,
}: {
  onResolve: (id: string) => Scenario | null;
}): JSX.Element {
  const [q, setQ] = useState("");
  const [miss, setMiss] = useState<string | null>(null);
  function submit(text: string): void {
    const t = text.trim();
    if (t.length < 4) { setMiss("Type a few more words."); return; }
    const id = routeFreeTextToScenario(t);
    if (!id) { setMiss("Try: late · budget · render · approval · rule."); return; }
    setMiss(null);
    onResolve(id);
    setQ("");
  }
  return (
    <label className="block flex-1 min-w-[260px]">
      <span className="text-[10px] uppercase tracking-wider text-ink-500">Or ask in your own words</span>
      <form
        onSubmit={(e) => { e.preventDefault(); submit(q); }}
        className="mt-1 flex items-stretch gap-1"
      >
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); if (miss) setMiss(null); }}
          placeholder="e.g. what if the chandelier is late?"
          className={cls(
            "flex-1 text-xs bg-cream border rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2",
            miss ? "border-foyr/40 ring-1 ring-foyr/20" : "border-border focus:ring-foyr/30",
          )}
        />
        <button type="submit" disabled={!q.trim()} className="btn btn-foyr !text-xs !py-2 shrink-0">
          Find + run
        </button>
      </form>
      {miss ? <div className="text-[10px] text-foyr-deep mt-1">{miss}</div> : null}
    </label>
  );
}

function ScenarioChips({
  scenarios, selectedId, onPick,
}: {
  scenarios: Scenario[];
  selectedId: string;
  onPick: (id: string) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-ink-500 shrink-0">Scenarios</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {scenarios.map((s) => {
          const active = s.id === selectedId;
          const u = scenarioUrgency(s.id);
          const tone =
            u >= 0.8 ? "live-active" :
            u >= 0.6 ? "live"        :
            "calm";
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              className={cls(
                "pill text-[11px] border inline-flex items-center gap-1.5 transition-shadow",
                active
                  ? "bg-ink-950 text-cream border-ink-950 shadow-sm"
                  : "bg-cream border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950 hover:shadow-sm",
              )}
              title={genScenarioSubtitle(s.id) ?? s.subtitle}
            >
              {tone === "live-active" ? (
                <span className="w-1.5 h-1.5 rounded-full bg-foyr animate-pulse" title="active right now" />
              ) : tone === "live" ? (
                <span className="w-1.5 h-1.5 rounded-full bg-fern" title="live" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-ink-300" />
              )}
              {/* Title is project-aware — walks the active graph at render time. */}
              {genScenarioTitle(s.id) ?? s.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────── Tab bar ─── */

function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }): JSX.Element {
  const sharedTabs = TABS.filter((t) => t.audience === "shared");
  const studioTabs = TABS.filter((t) => t.audience === "studio");
  return (
    <div className="mt-5 flex items-stretch gap-1 sm:gap-2 flex-wrap">
      {sharedTabs.map((t) => <TabPill key={t.id} tab={t} active={t.id === tab} onClick={() => onChange(t.id)} />)}
      <span
        className="self-stretch w-px bg-border/70 mx-1.5 hidden sm:inline-block"
        aria-hidden="true"
      />
      <span
        className="self-center text-[9.5px] uppercase tracking-wider text-ink-500 px-1 hidden md:inline-flex items-center gap-1"
        title="Studio-only — not exposed to the client share view"
      >
        studio
      </span>
      {studioTabs.map((t) => <TabPill key={t.id} tab={t} active={t.id === tab} onClick={() => onChange(t.id)} />)}
    </div>
  );
}

function TabPill({ tab: t, active, onClick }: { tab: typeof TABS[number]; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={t.sub}
      data-tab-audience={t.audience}
      className={cls(
        "inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full text-left transition-all",
        active
          ? "bg-ink-950 text-cream"
          : "bg-cream-dark border border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950",
      )}
    >
      <span className={cls(
        "grid place-items-center w-6 h-6 rounded-full shrink-0",
        active ? "bg-cream/15" : "bg-cream",
      )}>
        {t.icon}
      </span>
      <span className="flex flex-col leading-none">
        <span className="text-[12px] font-semibold">{t.label}</span>
        <span className={cls("text-[9.5px] mt-0.5", active ? "text-cream/70" : "text-ink-500")}>{t.sub}</span>
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────── Main pane ─── */

function MainPane({
  tab,
  highlightSet,
  onNodeFocus,
  appliedRuleIds,
  activeStageHint,
  focusedNodeId,
}: {
  tab: Tab;
  highlightSet: Set<string>;
  onNodeFocus: (id: string) => void;
  appliedRuleIds: string[];
  activeStageHint?: string | undefined;
  focusedNodeId: string;
}): JSX.Element {
  const appliedSet = useMemo(() => new Set(appliedRuleIds), [appliedRuleIds]);
  return (
    <main className="space-y-3 min-w-0">
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "rooms" ? <RoomsView /> : null}
          {tab === "graph" ? (
            <CockpitGraphTab
              highlightSet={highlightSet}
              focusedNodeId={focusedNodeId}
              onNodeFocus={onNodeFocus}
            />
          ) : null}
          {tab === "catalog" ? (
            <CatalogGrid highlightIds={highlightSet} onSelect={(s) => onNodeFocus(s.id)} />
          ) : null}
          {tab === "inspiration" ? (
            <div>
              <RenderRoomPanel />
              <MoodBoard />
            </div>
          ) : null}
          {tab === "rules"  ? <RulesPaneInline  appliedSet={appliedSet} /> : null}
          {tab === "memory" ? <MemoryDeck /> : null}
          {/* Datasets tab moved to /admin/datasets */}
          {tab === "agent"  ? <AgentPaneInline activeStageHint={activeStageHint} /> : null}
        </motion.div>
      </AnimatePresence>
    </main>
  );
}

/* ─── Rules pane on the cockpit · Watchlist + engineer fold-out ─── */

/* Cockpit Rules tab — project-aware *only*.
 *
 *   Library + Add-to-project   → top-menu /library
 *   Builder + dry-run + canvas → top-menu /rules
 *   Cockpit Rules tab          → which applied rules fired or stayed
 *                                clean on *this* project, with reason
 *
 * No Add-watch dialog, no engineer-view fold-out, no library duplicate. */
function RulesPaneInline({ appliedSet }: { appliedSet: ReadonlySet<string> }): JSX.Element {
  const { isLinkedPending } = useActiveProjectGraph();
  const meta = useActiveLinkedProject();
  if (isLinkedPending && meta) {
    return <BundlePending surface="rules" project={meta} />;
  }
  return <ProjectRuleStatus appliedIds={appliedSet} />;
}

function AgentPaneInline({ activeStageHint }: { activeStageHint: string | undefined }): JSX.Element {
  const { isLinkedPending } = useActiveProjectGraph();
  const meta = useActiveLinkedProject();
  if (isLinkedPending && meta) {
    return <BundlePending surface="agent" project={meta} />;
  }
  return <AgenticFlow activeStageHint={activeStageHint} />;
}

/* Reads the user-created (linked) project meta for the active id.
 * Lives here so the cockpit panels can pass the ProjectSummary to
 * BundlePending without each panel re-reading localStorage. */
function useActiveLinkedProject(): ProjectSummary | null {
  const { activeId } = useActiveProjectGraph();
  return useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      if (!raw) return null;
      const arr = JSON.parse(raw) as ProjectSummary[];
      return Array.isArray(arr) ? arr.find((p) => p.id === activeId) ?? null : null;
    } catch { return null; }
  }, [activeId]);
}

/* Cockpit Graph tab — *only* the in-context subgraph. The full
 * project map already lives at the top-menu /graph route, so we
 * don't duplicate it here.
 *
 * Focus auto-selection (so the picture is never empty):
 *   1. The currently focused node id, if it exists in the active graph.
 *   2. Else every Alert node in the active graph.
 *   3. Else the first node — the graph always has something to centre on.
 */
function CockpitGraphTab({
  highlightSet,
  focusedNodeId,
  onNodeFocus,
}: {
  highlightSet: Set<string>;
  focusedNodeId: string;
  onNodeFocus: (id: string) => void;
}): JSX.Element {
  const g = getActiveProjectGraph();

  const focusIds = useMemo(() => {
    const set = new Set<string>();
    const idSet = new Set(g.nodes.map((n) => n.id));
    for (const id of highlightSet) if (idSet.has(id)) set.add(id);
    if (focusedNodeId && idSet.has(focusedNodeId)) set.add(focusedNodeId);
    if (set.size === 0) {
      // Fall back to alerts on the active project — that's the
      // agent's natural "what am I touching right now?" anchor.
      for (const n of g.nodes) if (n.kind === "Alert") set.add(n.id);
    }
    if (set.size === 0 && g.nodes.length > 0) {
      const first = g.nodes[0];
      if (first) set.add(first.id);
    }
    return Array.from(set);
  }, [highlightSet, focusedNodeId, g]);

  const graph3d = useMemo(() => ({
    nodes: g.nodes.map<Graph3DNode>((n) => ({
      id: n.id, label: n.label, kind: n.kind.toLowerCase(),
      category: String(n.attrs?.["category"] ?? n.attrs?.["roomKind"] ?? n.attrs?.["zoneKind"] ?? ""),
    })),
    links: g.edges.map<Graph3DLink>((e) => ({
      source: e.source, target: e.target, label: e.kind,
      emphasis: /RULE_FIRED|BLOCKED_BY|DEPENDS_ON|RULE_RECOMMENDS/.test(e.kind) ? "primary" : "neutral",
    })),
  }), [g]);

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="eyebrow">In-context graph · {g.projectName ?? "live"}</div>
          <div className="text-[12px] text-ink-500">
            Centred on {focusIds.length} {focusIds.length === 1 ? "node" : "nodes"} · ≤ 2 hops out · ignores everything else on this project.
          </div>
        </div>
        <a
          href="/graph"
          className="pill bg-cream-dark border border-border text-ink-700 hover:text-ink-950 hover:border-foyr/40 transition text-[11px]"
        >
          Open full project map →
        </a>
      </div>
      <ExpandableGraphFrame
        title="Agent's working subgraph"
        subtitle={`${focusIds.length} focused · ≤ 2 hops · auto-trims unrelated context`}
        graph3d={graph3d}
      >
        <HyperGraphFlow
          highlightNodeIds={highlightSet}
          onNodeClick={(n) => onNodeFocus(n.id)}
          height={620}
          direction="LR"
          focusNodeIds={focusIds}
          subgraphHops={2}
        />
      </ExpandableGraphFrame>
    </div>
  );
}

/* ─────────────────────────────────────────── Side rail (Chat · Why · Activity) ─── */

type RailTab = "chat" | "client" | "inbox" | "why" | "activity";

const RAIL_TABS: { id: RailTab; label: string }[] = [
  { id: "chat",     label: "Copilot" },
  { id: "client",   label: "Client" },
  { id: "inbox",    label: "Inbox" },
  { id: "why",      label: "Why" },
  { id: "activity", label: "Activity" },
];

function SideRail({
  focusedNodeId,
  onFocusChange,
  events,
}: {
  focusedNodeId: string;
  onFocusChange: (id: string) => void;
  events: ScenarioStep[];
}): JSX.Element {
  const [tab, setTab] = useState<RailTab>("chat");
  return (
    <div className="space-y-3">
      <div className="panel p-1 flex items-center gap-0.5">
        {RAIL_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cls(
              "flex-1 px-1.5 py-1.5 rounded-lg text-[11px] font-medium transition-all",
              t.id === tab ? "bg-ink-950 text-cream" : "text-ink-700 hover:bg-border/60",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.15 }}
        >
          {tab === "chat" ? (
            <DesignerChat onNavigateToNode={onFocusChange} />
          ) : tab === "client" ? (
            <ClientCopilot />
          ) : tab === "inbox" ? (
            <InboxQueue onFocusNode={onFocusChange} />
          ) : tab === "why" ? (
            <WhyPanel focusedNodeId={focusedNodeId} onFocusChange={onFocusChange} />
          ) : (
            <ActivityFeed events={events} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ─────────────────────────────────────────── Activity feed ─── */

/** Format a UTC ISO instant as a relative-time string ("3m ago", "2h ago",
 *  "5d ago"). Honest about resolution; nothing rounded silently. Reads
 *  Date.now() at call time so the feed stays fresh without a tick. */
function relativeFromIso(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diffSec = Math.max(0, Math.round((now - t) / 1000));
  if (diffSec < 60)        return diffSec < 5 ? "just now" : `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60)        return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24)         return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30)        return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  return `${diffMo}mo ago`;
}

function ActivityFeed({ events }: { events: ScenarioStep[] }): JSX.Element {
  // Merge live scenario events with the pre-seeded recent history
  // so the feed shows real life even before any scenario plays.
  // CLAUDE.md §1 — seed data lives in src/data/activity.ts.
  type Row = {
    id: string;
    kind: string;
    label: string;
    detail: string;
    badge: "warn" | "info" | "ok" | "money" | undefined;
    delta: string | undefined;
    when: string;
    isLive: boolean;
  };
  // Pull typed :CatalogImport activities from the active project graph
  // — every ingest run becomes one row here, derived from the ontology,
  // not faked. The merged graph is hydrated in Design + useActiveProjectGraph.
  const { graph } = useActiveProjectGraph();
  const importRows: Row[] = useMemo(() => {
    const now = Date.now();
    return graph.nodes
      .filter((n) => n.kind === "CatalogImport")
      .map((n) => {
        const atTime = typeof n.attrs["atTime"] === "string" ? n.attrs["atTime"] as string : "";
        const source = typeof n.attrs["source"] === "string" ? n.attrs["source"] as string : "unknown";
        const datasetId = typeof n.attrs["datasetId"] === "string" ? n.attrs["datasetId"] as string : "—";
        const rowCount = typeof n.attrs["rowCount"] === "number" ? n.attrs["rowCount"] as number : 0;
        return {
          id: `ci-${n.id}`,
          kind: "catalog import",
          label: `${rowCount} SKU${rowCount === 1 ? "" : "s"} imported from ${datasetId}`,
          detail: `Typed :CatalogImport prov:Activity · source ${source}`,
          badge: "info" as const,
          delta: rowCount > 0 ? `+${rowCount} SKU` : undefined,
          when: atTime ? relativeFromIso(atTime, now) : "—",
          isLive: false,
          // Use atTime for sorting (newest first).
          _ts: atTime ? new Date(atTime).getTime() : 0,
        } as Row & { _ts: number };
      })
      .sort((a, b) => ((b as Row & { _ts: number })._ts) - ((a as Row & { _ts: number })._ts))
      .map(({ _ts: _drop, ...rest }) => rest);
  }, [graph.nodes]);

  const liveRows: Row[] = events.slice(-8).reverse().map((e, i) => ({
    id: `live-${e.id}`,
    kind: e.kind,
    label: e.label,
    detail: e.detail,
    badge: e.badge ?? "info",
    delta: e.delta,
    when: i === 0 ? "just now" : `${i * 2}s ago`,
    isLive: true,
  }));
  const seedRows: Row[] = SEED_ACTIVITY.map((s) => ({
    id: s.id,
    kind: s.kind,
    label: s.label,
    detail: s.detail,
    badge: s.badge,
    delta: s.delta,
    when: s.when,
    isLive: false,
  }));
  const rows = [...liveRows, ...importRows, ...seedRows];
  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">Activity</div>
        <span className="pill bg-cream-dark border border-border text-ink-500 font-mono">{rows.length}</span>
      </div>
      <ol className="relative border-l border-border pl-4 space-y-3">
        <AnimatePresence initial={false}>
          {rows.map((ev) => (
            <motion.li
              key={ev.id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="relative"
            >
              <span
                className={cls(
                  "absolute -left-[19px] top-1 w-2 h-2 rounded-full",
                  ev.badge === "warn"  ? "bg-foyr" :
                  ev.badge === "ok"    ? "bg-fern" :
                  ev.badge === "money" ? "bg-ink-950" :
                                         "bg-sea",
                )}
              />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-accent">{ev.kind}</span>
                {ev.isLive ? <span className="pill bg-fern/10 text-fern text-[9px]">live</span> : null}
                <span className="text-[10px] text-ink-400 ml-auto">{ev.when}</span>
              </div>
              <div className="text-xs text-ink-950 font-medium leading-snug">{ev.label}</div>
              <div className="text-[11px] text-ink-500 leading-snug">{ev.detail}</div>
              {ev.delta ? (
                <span className="inline-block mt-1 pill bg-foyr/10 text-foyr-deep tabular text-[10px]">{ev.delta}</span>
              ) : null}
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </div>
  );
}
